import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  AiVerificationQuestion,
  AiVerificationTemplate,
  DistrictMode,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { ModeSelectField, useAiCategoryMasters } from '../masters';
import {
  DefaultDistributionSection,
  type DefaultDistributionValue,
} from './DefaultDistributionSection';
import { PerClassificationEditor } from './PerClassificationEditor';
import { QuestionListEditor } from './QuestionListEditor';
import { updateVerificationTemplate, useVerificationTemplates } from './useVerificationTemplates';

/**
 * AI 検証テンプレ編集画面 (admin 第 23 弾 + Sprint AZ、Phase 5)。
 *
 * Sprint AZ (visual editor 拡張):
 *   - perPostQuestions / perClassificationQuestions の visual editor を追加
 *     (旧 `old/apps/admin/src/features/verification-admin/TemplateEditor*.tsx` の 6 type widget を踏襲)
 *   - JSON textarea fallback は「上級者」 tab として維持 (後方互換)
 *   - visual と JSON は「現在 active な tab の state」が canonical、tab 切替時に sync
 *   - 保存時は active な tab の値で保存 (visual を優先するため、デフォルトは visual)
 *
 * 基本情報 (displayName / description / targetMode / status / allowFreeComment) は従来通り form 編集。
 * defaultDistribution / defaultRandomConstraints / defaultCandidateCriteria /
 * defaultEndAtOffsetDays も従来通り JSON section (AssignmentSet generator が別 UI で provide)。
 *
 * 認可: master / district admin (担当地区) のみ操作可。
 */

type TemplateStatus = AiVerificationTemplate['status'];

const STATUS_LABEL: Record<TemplateStatus, string> = {
  draft: '下書き',
  published: '配信中',
  archived: 'アーカイブ',
};

/**
 * targetMode の保存値変換 (MD-3 モードドリブン化 2026-07-24)。
 * 旧実装は組込 4 種を whitelist し custom mode を null に落としていた (バグ) →
 * mode キーは ModeProfile 駆動の任意 string になったため、非空ならそのまま保存する。
 */
function asTargetMode(v: string): DistrictMode | null {
  const trimmed = v.trim();
  return trimmed ? trimmed : null;
}

type QuestionsEditorMode = 'visual' | 'json';

export function VerificationTemplateEditPage() {
  const { districtId, templateId } = useParams<{ districtId: string; templateId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const { toast } = useToast();

  const target = useMemo(() => {
    if (templatesState.status !== 'ready' || !templateId) return null;
    return templatesState.items.find((t) => t.id === templateId) ?? null;
  }, [templatesState, templateId]);

  // 基本情報
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [targetMode, setTargetMode] = useState<string>('');
  const [status, setStatus] = useState<TemplateStatus>('draft');
  const [allowFreeComment, setAllowFreeComment] = useState(true);
  // GAP-A12 (Sprint BE) — どの M2 aiCategoryMasters を対象とするか (空 = 全分類共通)
  const [classificationId, setClassificationId] = useState<string>('');
  const categoryMastersState = useAiCategoryMasters();

  // 質問群 — visual + JSON 2 系統
  const [perPostQuestions, setPerPostQuestions] = useState<AiVerificationQuestion[]>([]);
  const [perClassificationQuestions, setPerClassificationQuestions] = useState<
    AiVerificationQuestion[]
  >([]);
  const [perPostJson, setPerPostJson] = useState('[]');
  const [perClsJson, setPerClsJson] = useState('[]');
  const [perPostError, setPerPostError] = useState<string | null>(null);
  const [perClsError, setPerClsError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<QuestionsEditorMode>('visual');

  // 配分デフォルト (AW4 #8 — 生 JSON textarea → 専用フォーム DefaultDistributionSection)
  const [distribution, setDistribution] = useState<DefaultDistributionValue>({});

  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!target || initialized) return;
    setDisplayName(target.displayName);
    setDescription(target.description);
    setTargetMode(target.targetMode ?? '');
    setStatus(target.status);
    setAllowFreeComment(target.allowFreeComment);
    // GAP-A12 — classificationId は optional field、未設定なら空 string ('全分類共通' の placeholder)
    const targetWithCls = target as typeof target & { classificationId?: string | null };
    setClassificationId(
      typeof targetWithCls.classificationId === 'string' ? targetWithCls.classificationId : '',
    );
    setPerPostQuestions(target.perPostQuestions);
    setPerClassificationQuestions(target.perClassificationQuestions);
    setPerPostJson(JSON.stringify(target.perPostQuestions, null, 2));
    setPerClsJson(JSON.stringify(target.perClassificationQuestions, null, 2));
    const dist: DefaultDistributionValue = {};
    if (target.defaultDistribution) dist.defaultDistribution = target.defaultDistribution;
    if (target.defaultRandomConstraints)
      dist.defaultRandomConstraints = target.defaultRandomConstraints;
    if (target.defaultCandidateCriteria)
      dist.defaultCandidateCriteria = target.defaultCandidateCriteria;
    if (typeof target.defaultEndAtOffsetDays === 'number') {
      dist.defaultEndAtOffsetDays = target.defaultEndAtOffsetDays;
    }
    setDistribution(dist);
    setInitialized(true);
  }, [target, initialized]);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  function tryParseQuestions(
    text: string,
    setErr: (e: string | null) => void,
  ): AiVerificationQuestion[] | null {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!Array.isArray(parsed)) throw new Error('配列ではありません');
      for (const q of parsed) {
        if (!q || typeof q !== 'object' || typeof (q as { id?: unknown }).id !== 'string') {
          throw new Error('各要素は id(string) を含む object である必要があります');
        }
      }
      setErr(null);
      return parsed as AiVerificationQuestion[];
    } catch (e) {
      setErr((e as Error).message);
      return null;
    }
  }

  /**
   * tab 切替: visual → json なら visual state を JSON 文字列に sync、
   * json → visual なら JSON を parse → visual state に sync (parse 失敗時は切替えない)。
   */
  function switchEditorMode(next: QuestionsEditorMode) {
    if (next === editorMode) return;
    if (next === 'json') {
      setPerPostJson(JSON.stringify(perPostQuestions, null, 2));
      setPerClsJson(JSON.stringify(perClassificationQuestions, null, 2));
      setPerPostError(null);
      setPerClsError(null);
      setEditorMode('json');
    } else {
      const pp = tryParseQuestions(perPostJson, setPerPostError);
      const pc = tryParseQuestions(perClsJson, setPerClsError);
      if (!pp || !pc) {
        toast('error', 'JSON にエラーがあります。修正後に visual に切替えてください。');
        return;
      }
      setPerPostQuestions(pp);
      setPerClassificationQuestions(pc);
      setEditorMode('visual');
    }
  }

  async function handleSave() {
    if (!districtId || !templateId || !target || !canManage) return;

    let perPost: AiVerificationQuestion[];
    let perCls: AiVerificationQuestion[];
    if (editorMode === 'visual') {
      perPost = perPostQuestions;
      perCls = perClassificationQuestions;
    } else {
      const pp = tryParseQuestions(perPostJson, setPerPostError);
      const pc = tryParseQuestions(perClsJson, setPerClsError);
      if (!pp || !pc) {
        toast('error', 'JSON にエラーがあります。修正してください。');
        return;
      }
      perPost = pp;
      perCls = pc;
    }

    const dist = distribution;
    setSubmitting(true);
    try {
      await updateVerificationTemplate(
        districtId,
        templateId,
        {
          displayName: displayName.trim(),
          description,
          targetMode: asTargetMode(targetMode),
          perPostQuestions: perPost,
          perClassificationQuestions: perCls,
          allowFreeComment,
          status,
          // GAP-A12 — classificationId は空文字列なら null (全分類共通)、それ以外は M2 master id
          classificationId: classificationId.trim() === '' ? null : classificationId.trim(),
          ...(dist.defaultDistribution !== undefined
            ? { defaultDistribution: dist.defaultDistribution }
            : {}),
          ...(dist.defaultRandomConstraints !== undefined
            ? { defaultRandomConstraints: dist.defaultRandomConstraints }
            : {}),
          ...(dist.defaultCandidateCriteria !== undefined
            ? { defaultCandidateCriteria: dist.defaultCandidateCriteria }
            : {}),
          ...(typeof dist.defaultEndAtOffsetDays === 'number'
            ? { defaultEndAtOffsetDays: dist.defaultEndAtOffsetDays }
            : {}),
        },
        uid,
        target.version,
      );
      // 保存後、もう一方の tab の表示値を最新に sync
      if (editorMode === 'visual') {
        setPerPostJson(JSON.stringify(perPost, null, 2));
        setPerClsJson(JSON.stringify(perCls, null, 2));
      } else {
        setPerPostQuestions(perPost);
        setPerClassificationQuestions(perCls);
      }
      toast('success', '検証テンプレを保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  function handlePerPostChange(text: string) {
    setPerPostJson(text);
    tryParseQuestions(text, setPerPostError);
  }

  function handlePerClsChange(text: string) {
    setPerClsJson(text);
    tryParseQuestions(text, setPerClsError);
  }

  const hasJsonErrors = editorMode === 'json' ? !!(perPostError || perClsError) : false;
  const hasErrors = hasJsonErrors;

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}/verification/templates` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 検証テンプレ一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          検証テンプレ編集
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          基本情報 + 質問群 (visual editor / JSON fallback) + 配分デフォルト (JSON)。
          {districtId && (
            <>
              {' '}
              AssignmentSet 配分実行は{' '}
              <Link
                to={`/districts/${districtId}/verification/distribute`}
                className="text-primary-600 hover:underline"
              >
                配分実行ページ
              </Link>{' '}
              から。
            </>
          )}
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の検証テンプレ管理権限がありません (role: {authState.role ?? 'none'})。
          </p>
        </Card>
      )}

      {templatesState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {templatesState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {templatesState.error.message}</p>
        </Card>
      )}

      {templatesState.status === 'ready' && !target && (
        <Card padding="md">
          <p className="text-sm text-slate-600">
            テンプレートが見つかりません (id: {templateId})。
          </p>
        </Card>
      )}

      {target && (
        <>
          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">基本情報</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">表示名 *</span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  disabled={!canManage || submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">説明</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  disabled={!canManage || submitting}
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">対象モード</span>
                  {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
                  <ModeSelectField
                    value={targetMode}
                    onChange={(m) => setTargetMode(m)}
                    disabled={!canManage || submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                    emptyOptionLabel="全モード共通 (null)"
                  />
                </label>
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">状態</span>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TemplateStatus)}
                    disabled={!canManage || submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="draft">{STATUS_LABEL.draft}</option>
                    <option value="published">{STATUS_LABEL.published}</option>
                    <option value="archived">{STATUS_LABEL.archived}</option>
                  </select>
                </label>
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={allowFreeComment}
                  onChange={(e) => setAllowFreeComment(e.target.checked)}
                  disabled={!canManage || submitting}
                  className="mt-1"
                />
                <span>自由記述コメント (1 投稿 1 件、最大 500 字想定) を許可</span>
              </label>

              {/* GAP-A12 (Sprint BE) — 分類軸スコープ (M2 aiCategoryMasters への参照) */}
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">対象分類軸 (M2 aiCategoryMasters)</span>
                {categoryMastersState.status === 'loading' && (
                  <p className="text-xs text-slate-400">分類軸マスター読込中…</p>
                )}
                {categoryMastersState.status === 'error' && (
                  <p className="text-xs text-danger-700">
                    分類軸マスター読込エラー: {categoryMastersState.error.message}
                  </p>
                )}
                {categoryMastersState.status === 'ready' && (
                  <>
                    <select
                      value={classificationId}
                      onChange={(e) => setClassificationId(e.target.value)}
                      disabled={!canManage || submitting}
                      aria-label="対象分類軸 (classificationId)"
                      className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                    >
                      <option value="">— 全分類共通 (null) —</option>
                      {categoryMastersState.items.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.displayName} ({m.mode} / id: {m.id})
                        </option>
                      ))}
                    </select>
                    <span className="text-[11px] text-slate-400">
                      GAP-A12 — perClassificationQuestions の `appliesToRanks`
                      がランク絞込なのに対し、 `classificationId`
                      は「どの分類軸に対する検証か」を限定します。 未選択 = 旧挙動 (全分類共通)。
                    </span>
                  </>
                )}
              </label>
            </div>
          </Card>

          <Card padding="lg" className="mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-base font-bold text-slate-900">質問群</h2>
              <div
                role="tablist"
                aria-label="質問群編集モード"
                className="flex border-b border-slate-200"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={editorMode === 'visual'}
                  onClick={() => switchEditorMode('visual')}
                  className={`px-3 py-1.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
                    editorMode === 'visual'
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  ビジュアル
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={editorMode === 'json'}
                  onClick={() => switchEditorMode('json')}
                  className={`px-3 py-1.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
                    editorMode === 'json'
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  JSON (上級者)
                </button>
              </div>
            </div>

            {editorMode === 'visual' ? (
              <div className="grid grid-cols-1 gap-6">
                <section>
                  <h3 className="text-sm font-bold text-slate-800 mb-2">
                    投稿全体への質問 (perPostQuestions)
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">
                    投稿 1 件に対して 1 回だけ問う質問。AI の全体評価 (description /
                    professional_insight / score 等) の妥当性確認に用います。
                  </p>
                  <QuestionListEditor
                    scope="perPost"
                    value={perPostQuestions}
                    onChange={setPerPostQuestions}
                    disabled={!canManage || submitting}
                  />
                </section>
                <section className="border-t border-slate-200 pt-4">
                  <h3 className="text-sm font-bold text-slate-800 mb-2">
                    分類行ごとの質問 (perClassificationQuestions)
                  </h3>
                  {/* Sprint BG-DataSettings 11 / GAP-A11 — JSON fallback を visual editor に置換。
                      PerClassificationEditor は QuestionListEditor + GAP-A12 用の補足文を内包 */}
                  <PerClassificationEditor
                    value={perClassificationQuestions}
                    onChange={setPerClassificationQuestions}
                    disabled={!canManage || submitting}
                  />
                </section>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">
                    投稿全体への質問 (perPostQuestions)
                  </h3>
                  <p className="text-xs text-slate-500 mb-2">
                    AiVerificationQuestion[] の配列を JSON で直接編集。各要素は <code>id</code>{' '}
                    (string) を持ち、<code>type</code> は{' '}
                    <code>
                      three_state | binary | five_scale | yes_no | comment | custom_choice
                    </code>{' '}
                    から選択。
                  </p>
                  <textarea
                    value={perPostJson}
                    onChange={(e) => handlePerPostChange(e.target.value)}
                    rows={10}
                    disabled={!canManage || submitting}
                    aria-label="perPostQuestions JSON"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
                    spellCheck={false}
                  />
                  {perPostError && (
                    <p className="text-[11px] text-danger-700 mt-1">JSON エラー: {perPostError}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">
                    分類行ごとの質問 (perClassificationQuestions)
                  </h3>
                  <p className="text-xs text-slate-500 mb-2">
                    bousai / walker mode の classifications 配列に対する質問。
                    <code>appliesToRanks</code> で対象 rank を絞り込み (
                    <code>&apos;top&apos; | &apos;top3&apos; | &apos;all&apos; | number[]</code>)。
                  </p>
                  <textarea
                    value={perClsJson}
                    onChange={(e) => handlePerClsChange(e.target.value)}
                    rows={10}
                    disabled={!canManage || submitting}
                    aria-label="perClassificationQuestions JSON"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
                    spellCheck={false}
                  />
                  {perClsError && (
                    <p className="text-[11px] text-danger-700 mt-1">JSON エラー: {perClsError}</p>
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">配分デフォルト</h2>
            <p className="text-xs text-slate-500 mb-3">
              AssignmentSet 作成時の推奨値。実際の AssignmentSet 配分実行は配分セット画面
              (generateAssignments callable) から。
            </p>
            {/* AW4 (docs/108 High #8) — 生 JSON textarea を専用フォームに置換 */}
            <DefaultDistributionSection
              value={distribution}
              onChange={setDistribution}
              disabled={!canManage || submitting}
            />
          </Card>

          <div className="flex justify-end gap-2 mb-6">
            <Link
              to={`/districts/${districtId}/verification/templates`}
              className="min-h-touch flex items-center px-4 text-sm text-slate-600 hover:text-slate-800"
            >
              キャンセル
            </Link>
            <Button
              onClick={() => void handleSave()}
              disabled={!canManage || submitting || hasErrors}
            >
              {submitting ? '保存中…' : '💾 保存'}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
